import {
  ML_SCRIBE_PREVIEW_HOSTNAMES,
  scribeDomains,
  scribeWebsocketDomains,
} from './domains';
import { ENV_NAME, getEnvironment } from './env';
import { defaultReadEnv } from './read-env';
import { Region } from './region';
import type {
  ApiConfigOptions,
  ApiEndpointVersion,
  MlScribePreviewConfig,
  ScribeDomainUrls,
} from './types';

/**
 * Query params that retarget the app at a preview backend instead of the shared
 * dev one, e.g. `?ml-scribe=pr-17778`.
 *
 * Exported because the desktop app has no address bar: `scripts/dev.ts` takes
 * `--ml-scribe <branch>` and appends it to the URL the WebView opens. Both
 * sides must agree on the names and on what a valid value looks like, so they
 * read them from here rather than each keeping a copy.
 */
export const ML_SCRIBE_QUERY_PARAM = 'ml-scribe';
export const TRANSCRIPTION_QUERY_PARAM = 'transcription';

/**
 * A preview value is the deployment name, which ml-scribe's pipeline-preview.yml
 * derives from the PR rather than the branch: `K8S_INSTANCE="pr-${PR_NUMBER}"`.
 * So the value is `pr-17778`, not a branch name.
 *
 * The pattern is a path-injection guard, not a naming rule — the value is
 * interpolated straight into `https://${previewHostname}/${previewPath}`, so a
 * `/` would let it climb out of its path segment. Callers should reject a bad
 * value rather than silently ignoring it.
 */
export const PREVIEW_PARAM_PATTERN = /^[a-zA-Z0-9-]+$/;

const defaultWidgetEhrBaseUrl: Record<ENV_NAME, string> = {
  [ENV_NAME.LOCALHOST]: 'https://registrar.widget.heidihealth.com/ehr',
  [ENV_NAME.DEV]: 'https://registrar.widget.heidihealth.com/ehr',
  [ENV_NAME.STAGING]: 'https://registrar.widget.heidihealth.com/ehr',
  [ENV_NAME.PROD]: 'https://widget.heidihealth.com/ehr',
  [ENV_NAME.AZURE_DEV]: 'https://registrar.widget.heidihealth.com/ehr',
};

export type ApiConfig = ReturnType<typeof createApiConfig>;

export function createApiConfig(options: ApiConfigOptions = {}) {
  const isOnPrem = options.isOnPrem ?? false;
  const isDomainRewriteEnabled = options.isDomainRewriteEnabled ?? (() => false);
  const readEnv = options.readEnv ?? defaultReadEnv;

  let cachedMlScribePreviewParam: string | null | undefined;
  let cachedTranscriptionPreviewParam: string | null | undefined;

  const handleDomainRewrite = (domain: string) => {
    const isEKSDomain = domain.includes('.aws.ext');
    if (isEKSDomain && isDomainRewriteEnabled()) {
      return domain.replace('.heidihealth.com', '.heidi.ai');
    }
    return domain;
  };

  const getMlScribePreviewPath = (
    region: Region,
    environment: ENV_NAME
  ): string | null => {
    if (typeof window === 'undefined') return null;

    if (environment !== ENV_NAME.DEV || !ML_SCRIBE_PREVIEW_HOSTNAMES[region]) return null;

    if (cachedMlScribePreviewParam !== undefined) {
      return cachedMlScribePreviewParam;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const param = urlParams.get(ML_SCRIBE_QUERY_PARAM);

    if (!param || !PREVIEW_PARAM_PATTERN.test(param)) {
      cachedMlScribePreviewParam = null;
      return null;
    }

    cachedMlScribePreviewParam = param;
    return param;
  };

  const getMlScribePreviewConfig = (
    region: Region,
    environment: ENV_NAME
  ): MlScribePreviewConfig | null => {
    const previewPath = getMlScribePreviewPath(region, environment);
    if (!previewPath) return null;

    return {
      value: previewPath,
    };
  };

  const getTranscriptionPreviewParam = (
    region: Region,
    environment: ENV_NAME
  ): string | null => {
    if (typeof window === 'undefined') return null;

    if (environment !== ENV_NAME.DEV || region !== Region.AU) return null;

    if (cachedTranscriptionPreviewParam !== undefined) {
      return cachedTranscriptionPreviewParam;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const param = urlParams.get(TRANSCRIPTION_QUERY_PARAM);

    if (!param || !PREVIEW_PARAM_PATTERN.test(param)) {
      cachedTranscriptionPreviewParam = null;
      return null;
    }

    cachedTranscriptionPreviewParam = param;
    return param;
  };

  /**
   * Resolve (and cache) preview query params for the page session.
   * Does not print the console banner — the app layer does that via
   * `originalConsoleMethods` so Datadog's console interceptor cannot mangle it.
   */
  const resolvePreviewEnvironmentParams = (
    region: Region
  ): { mlScribe: string | null; transcription: string | null } => {
    if (typeof window === 'undefined') {
      return { mlScribe: null, transcription: null };
    }
    const environment = getEnvironment();
    return {
      mlScribe: getMlScribePreviewPath(region, environment),
      transcription: getTranscriptionPreviewParam(region, environment),
    };
  };

  const getUrlFromConfig = (region: Region) => {
    if (isOnPrem && typeof window !== 'undefined') {
      // Tauri desktop: window.location.origin is https://localhost (WebView),
      // not the on-prem server. Use the user-configured server URL instead.
      // Web: window.location.origin is the K8s ingress URL (correct).
      if (options.getOnPremServerUrl) {
        const serverUrl = options.getOnPremServerUrl();
        if (serverUrl) return serverUrl;
      }
      return window.location.origin;
    }

    const apiBaseUrl = readEnv('NEXT_PUBLIC_API_BASE_URL');
    if (apiBaseUrl) {
      return apiBaseUrl;
    }

    const environment = getEnvironment();

    const previewPath = getMlScribePreviewPath(region, environment);
    if (previewPath) {
      return handleDomainRewrite(
        `https://${ML_SCRIBE_PREVIEW_HOSTNAMES[region]}/${previewPath}`
      );
    }

    const regionConfig = scribeDomains[region] ?? scribeDomains[Region.AU];
    return handleDomainRewrite(regionConfig[environment]);
  };

  const getWebsocketUrlFromConfig = (region: Region) => {
    if (isOnPrem && typeof window !== 'undefined') {
      // Tauri desktop: use user-configured server URL for WebSocket too.
      if (options.getOnPremWebSocketUrl) {
        const wsUrl = options.getOnPremWebSocketUrl();
        if (wsUrl) return wsUrl;
      }
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${protocol}//${window.location.host}`;
    }

    const wsBaseUrl = readEnv('NEXT_PUBLIC_WS_BASE_URL');
    if (wsBaseUrl) {
      return wsBaseUrl;
    }

    const environment = getEnvironment();

    const previewPath = getMlScribePreviewPath(region, environment);
    if (previewPath) {
      return handleDomainRewrite(
        `wss://${ML_SCRIBE_PREVIEW_HOSTNAMES[region]}/${previewPath}`
      );
    }

    const regionConfig =
      scribeWebsocketDomains[region] ?? scribeWebsocketDomains[Region.AU];
    return handleDomainRewrite(regionConfig[environment]);
  };

  const getWidgetEhrBaseUrl = (): string => {
    const override = readEnv('NEXT_PUBLIC_WIDGET_EHR_BASE_URL');
    if (override) {
      return override;
    }
    return defaultWidgetEhrBaseUrl[getEnvironment()];
  };

  const getDomains = (region: Region): ScribeDomainUrls => {
    const baseUrl = getUrlFromConfig(region);
    const baseWebsocketUrl = getWebsocketUrlFromConfig(region);

    return {
      baseV1Url: baseUrl + '/api/v1/ml-scribe/',
      baseV1IdentityBrokerUrl: baseUrl + '/api/v1/identity-broker/',
      baseV2Url: baseUrl + '/api/v2/ml-scribe/',
      openApiBaseUrl: baseUrl + '/api/v2/ml-scribe/open-api/',
      baseV2_1Url: baseUrl + '/api/v2-1/ml-scribe/',
      baseV2_2Url: baseUrl + '/api/v2-2/ml-scribe/',
      websocketV1Url: baseWebsocketUrl,
    };
  };

  const getDomain = (region: Region, endPointVersion: ApiEndpointVersion) => {
    const baseUrl = getUrlFromConfig(region);
    return baseUrl + `/api/${endPointVersion}/ml-scribe/`;
  };

  return {
    getUrlFromConfig,
    getWebsocketUrlFromConfig,
    handleDomainRewrite,
    getMlScribePreviewConfig,
    getTranscriptionPreviewParam,
    resolvePreviewEnvironmentParams,
    getWidgetEhrBaseUrl,
    getDomains,
    getDomain,
  };
}
